import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { fmtDateDay } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Clock, MapPin, Phone, Mail, ArrowRight, Heart, Users, BookOpen,
  Calendar, Megaphone, Send, Loader2, CheckCircle2,
} from "lucide-react";

const DEFAULT_SITE = {
  church_name: "World Harvest Fellowship",
  tagline: "An Evangelical Free Church",
  welcome: "At World Harvest Fellowship we believe church is more than a service — it's a family.",
  service_times: [
    { day: "Sunday", name: "Morning Worship Service", time: "10:00 AM – 12:00 PM" },
    { day: "Wednesday", name: "Midweek Bible Study", time: "7:00 PM – 8:30 PM" },
    { day: "Friday", name: "Prayer & Fellowship", time: "6:30 PM – 8:00 PM" },
  ],
  contact: { address: "Auckland, New Zealand", phone: "(09) 123-4567", email: "hello@whfauckland.org" },
};
const SERVICE_ICONS = [Heart, BookOpen, Users];


export default function Landing() {
  const navigate = useNavigate();
  const [site, setSite] = useState(DEFAULT_SITE);
  const [events, setEvents] = useState([]);
  const [notices, setNotices] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", kind: "message", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/public/site").then((r) => setSite({ ...DEFAULT_SITE, ...r.data })).catch(() => {});
    api.get("/public/events").then((r) => { setEvents(r.data.events || []); setNotices(r.data.notices || []); }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSending(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      setForm({ name: "", email: "", kind: "message", message: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F5] text-[#1C241B]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/whf-emblem.png" alt="World Harvest Fellowship" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <p className="font-serif text-base font-semibold">{site.church_name}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#7A8A78]">Auckland</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="#services" className="hidden sm:inline text-sm font-medium text-[#526050] hover:text-[#2D5A27] px-3">Services</a>
            <a href="#events" className="hidden sm:inline text-sm font-medium text-[#526050] hover:text-[#2D5A27] px-3">Events</a>
            <a href="#contact" className="hidden sm:inline text-sm font-medium text-[#526050] hover:text-[#2D5A27] px-3">Contact</a>
            <Button data-testid="landing-signup-button" variant="outline" onClick={() => navigate("/register")} className="h-10 border-[#2D5A27] text-[#2D5A27] hover:bg-[#EAF2E8]">
              Sign Up
            </Button>
            <Button data-testid="landing-login-button" onClick={() => navigate("/login")} className="h-10 bg-[#2D5A27] hover:bg-[#23471F] text-white">
              Member Login
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative bg-black text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20 sm:py-28 flex flex-col items-center text-center">
          <img src="/whf-emblem.png" alt="" className="h-24 w-24 object-contain mb-6" />
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E7C65A]">{site.tagline}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-6xl font-bold leading-tight">
            {site.church_name}<br /><span className="text-[#E7C65A]">Auckland</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-stone-300">
            A warm family of believers growing together in faith, worship, and service.
            Wherever you are on your journey, there is a place for you here.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-3">
            <Button data-testid="hero-services-button" onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              className="h-12 px-7 bg-[#2D5A27] hover:bg-[#23471F] text-white text-base">
              Plan Your Visit <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <Button data-testid="hero-signup-button" onClick={() => navigate("/register")}
              className="h-12 px-7 bg-[#E7C65A] hover:bg-[#D9B544] text-[#1C241B] font-semibold text-base">
              Create Member Account
            </Button>
            <Button data-testid="hero-login-button" variant="outline" onClick={() => navigate("/login")}
              className="h-12 px-7 border-white/30 bg-transparent text-white hover:bg-white/10 text-base">
              Member Login
            </Button>
          </div>
        </div>
      </section>

      {/* Welcome */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">Welcome Home</p>
        <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">You're invited to belong</h2>
        <p className="mt-6 text-base sm:text-lg leading-relaxed text-[#526050]">{site.welcome}</p>
      </section>

      {/* Service Times */}
      <section id="services" className="bg-[#F3EFEA] py-16 sm:py-20 border-y border-[#E5E0D8]">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">Join Us</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">Service Times</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(site.service_times || []).map((s, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <div key={i} className="rounded-2xl bg-white border border-[#E5E0D8] shadow-sm p-7 text-center" data-testid={`service-${(s.day || "").toLowerCase()}`}>
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-[#2D5A27] flex items-center justify-center"><Icon className="h-7 w-7 text-white" /></div>
                  <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-[#D97706]">{s.day}</p>
                  <h3 className="mt-2 font-serif text-xl font-semibold">{s.name}</h3>
                  <p className="mt-2 flex items-center justify-center gap-2 text-[#526050]"><Clock className="h-4 w-4" /> {s.time}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Events & Announcements */}
      {(events.length > 0 || notices.length > 0) && (
        <section id="events" className="max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">What's Happening</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">Events & Announcements</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {events.map((e) => (
              <div key={e.id} className="rounded-2xl bg-white border border-[#E5E0D8] shadow-sm p-6" data-testid={`event-${e.id}`}>
                <div className="flex items-center gap-2 text-[#D97706]"><Calendar className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-widest">{fmtDateDay(e.date)}{e.time ? ` · ${e.time}` : ""}</span>
                </div>
                <h3 className="mt-3 font-serif text-xl font-semibold">{e.title}</h3>
                <p className="mt-2 text-sm text-[#526050]">{e.description}</p>
                {e.location && <p className="mt-3 flex items-center gap-1.5 text-sm text-[#7A8A78]"><MapPin className="h-4 w-4" />{e.location}</p>}
              </div>
            ))}
            {notices.map((n) => (
              <div key={n.id} className="rounded-2xl bg-[#EAF2E8] border border-[#cfe3cb] p-6" data-testid={`notice-${n.id}`}>
                <div className="flex items-center gap-2 text-[#2D5A27]"><Megaphone className="h-4 w-4" />
                  <span className="text-xs font-semibold uppercase tracking-widest">Notice</span>
                </div>
                <h3 className="mt-3 font-serif text-xl font-semibold">{n.title}</h3>
                <p className="mt-2 text-sm text-[#526050]">{n.description}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="bg-[#F3EFEA] border-y border-[#E5E0D8] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#2D5A27]">Get In Touch</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold">We'd love to hear from you</h2>
            <p className="mt-5 text-base leading-relaxed text-[#526050]">
              Have a question, a prayer request, or want to know more about our church family? Reach out any time.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4 rounded-xl bg-white border border-[#E5E0D8] p-5" data-testid="contact-address">
                <div className="h-11 w-11 rounded-xl bg-[#EAF2E8] flex items-center justify-center shrink-0"><MapPin className="h-5 w-5 text-[#2D5A27]" /></div>
                <div><p className="font-medium">Visit Us</p><p className="text-sm text-[#526050]">{site.contact?.address}</p></div>
              </div>
              <div className="flex items-start gap-4 rounded-xl bg-white border border-[#E5E0D8] p-5" data-testid="contact-phone">
                <div className="h-11 w-11 rounded-xl bg-[#EAF2E8] flex items-center justify-center shrink-0"><Phone className="h-5 w-5 text-[#2D5A27]" /></div>
                <div><p className="font-medium">Call Us</p><p className="text-sm text-[#526050]">{site.contact?.phone}</p></div>
              </div>
              <div className="flex items-start gap-4 rounded-xl bg-white border border-[#E5E0D8] p-5" data-testid="contact-email">
                <div className="h-11 w-11 rounded-xl bg-[#EAF2E8] flex items-center justify-center shrink-0"><Mail className="h-5 w-5 text-[#2D5A27]" /></div>
                <div><p className="font-medium">Email Us</p><p className="text-sm text-[#526050]">{site.contact?.email}</p></div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-[#E5E0D8] shadow-sm p-7">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10" data-testid="contact-success">
                <CheckCircle2 className="h-12 w-12 text-[#2D5A27]" />
                <h3 className="mt-4 font-serif text-2xl font-semibold">Message sent</h3>
                <p className="mt-2 text-sm text-[#526050]">Thank you — we'll be in touch soon.</p>
                <Button variant="outline" className="mt-6 border-[#2D5A27] text-[#2D5A27]" onClick={() => setSent(false)} data-testid="contact-send-another">Send another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                <h3 className="font-serif text-2xl font-semibold">Send us a message</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5"><Label>Your name</Label>
                    <Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
                  <div className="space-y-1.5"><Label>Email</Label>
                    <Input type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
                </div>
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="contact-kind-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="message">General Message</SelectItem>
                      <SelectItem value="prayer">Prayer Request</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Message</Label>
                  <Textarea data-testid="contact-message-input" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required /></div>
                {error && <p className="text-sm text-destructive">{error}</p>}
                <Button type="submit" disabled={sending} data-testid="contact-submit-button" className="w-full h-11 bg-[#2D5A27] hover:bg-[#23471F] text-white">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-2" /> Send Message</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-[#1A2619] text-stone-300">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/whf-emblem.png" alt="" className="h-9 w-9 object-contain" />
            <div className="leading-tight">
              <p className="font-serif text-base font-semibold text-stone-50">{site.church_name}</p>
              <p className="text-[10px] uppercase tracking-widest text-[#E7C65A]">Auckland · {site.tagline}</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-xs text-stone-400">© {new Date().getFullYear()} {site.church_name}</span>
            <Button data-testid="footer-signup-button" variant="outline" size="sm" onClick={() => navigate("/register")}
              className="border-white/20 bg-transparent text-stone-100 hover:bg-white/10">
              Sign Up
            </Button>
            <Button data-testid="footer-login-button" variant="outline" size="sm" onClick={() => navigate("/login")}
              className="border-white/20 bg-transparent text-stone-100 hover:bg-white/10">
              Member Login
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
}
